import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Paper,
  Grid,
  IconButton,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// import { useCallback } from "react";

const purpleBtnStyle = {
  background: "linear-gradient(135deg, #9b5cff, #8a4cf6)",
  color: "#fff",
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "12px",
  padding: "10px 24px",
  "&:hover": {
    background: "linear-gradient(135deg, #8a4cf6, #7b3fe4)",
  },
};

const BulkLectures = () => {
  const token = localStorage.getItem("token");

  const [batch, setBatch] = useState("");
  const [language, setLanguage] = useState("");
  const [subject, setSubject] = useState("");
  const [teacher, setTeacher] = useState("");

  const [batches, setBatches] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);

  const [lectures, setLectures] = useState([
    { title: "", videoUrl: "", status: "Active" },
  ]);

  const [loading, setLoading] = useState(false);

  const axiosConfig = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);

      const [batchRes, subjectRes, teacherRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_LIVE_URL}/adminPanel/getBatchDropdownList`,
          axiosConfig
        ),
        axios.get(
          `${process.env.REACT_APP_LIVE_URL}/adminPanel/getSubjectDetails`,
          axiosConfig
        ),
        axios.get(
          `${process.env.REACT_APP_LIVE_URL}/adminTeacher/teacherList`,
          axiosConfig
        ),
      ]);

      setBatches(batchRes.data?.data || []);
      setSubjects(subjectRes.data?.data || []);
      setTeachers(teacherRes.data?.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load dropdown data");
    } finally {
      setLoading(false);
    }
  };

  const addLecture = () => {
    if (lectures.length >= 20) {
      toast.error("You can upload only 20 lectures at a time");
      return;
    }

    setLectures([
      ...lectures,
      { title: "", videoUrl: "", status: "Active" },
    ]);
  };

  const removeLecture = (index) => {
    setLectures(lectures.filter((_, i) => i !== index));
  };

  const handleLectureChange = (index, field, value) => {
    const updated = [...lectures];
    updated[index][field] = value;
    setLectures(updated);
  };

  const handleSubmit = async () => {
    if (!batch || !subject || !teacher || !language) {
      toast.error("All top fields are required");
      return;
    }
    if (
      lectures.some(
        (l) => !l.title.trim() || !l.videoUrl.trim() || !l.status
      )
    ) {
      toast.error("All lecture fields are mandatory");
      return;
    }

    const payload = {
      batch: batch,
      subject_id: subject,
      teacher: teacher,
      language,
      lectures: lectures.map((l) => ({
        lecture_title: l.title,
        link: l.videoUrl,
        isActive: l.status === "Active",
      })),
    };

    try {
      setLoading(true);

      const res = await axios.post(
        `${process.env.REACT_APP_LIVE_URL}/adminPanel/addBulkCenterLectures`,
        payload,
        axiosConfig
      );

      if (res.data?.status) {
        toast.success("Bulk lectures added successfully");
        setTimeout(() => window.location.reload(), 600);
      } else {
        toast.error("Failed to add lectures");
      }
    } catch (error) {
      console.error(error);
      toast.error("Server error while saving lectures");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h4" mb={3}>
        Upload Bulk Lectures
      </Typography>

      <Paper sx={{ p: 3 }}>
        {loading && (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress />
          </Box>
        )}

        <TextField
          required
          select
          fullWidth
          label="Select Batch"
          value={batch}
          onChange={(e) => setBatch(e.target.value)}
          margin="normal"
        >
          <MenuItem value="">
            <em>Select Batch</em>
          </MenuItem>

          {batches.map((b) => (
            <MenuItem key={b._id} value={b._id}>
              {b.batch_name || b.name || b.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          required
          select
          fullWidth
          label="Language"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          margin="normal"
        >
          <MenuItem value="en">English</MenuItem>
          <MenuItem value="hi">Hindi</MenuItem>
          <MenuItem value="enhi">Bilingual</MenuItem>
        </TextField>

        <TextField
          required
          select
          fullWidth
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          margin="normal"
        >
          <MenuItem value="">
            <em>Select Subject</em>
          </MenuItem>

          {subjects.map((s) => (
            <MenuItem key={s._id} value={s._id}>
              {s.title}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          required
          select
          fullWidth
          label="Teacher"
          value={teacher}
          onChange={(e) => setTeacher(e.target.value)}
          margin="normal"
        >
          <MenuItem value="">
            <em>Select Teacher</em>
          </MenuItem>

          {teachers.map((t) => (
            <MenuItem key={t._id} value={t._id}>
              {t.FullName}
            </MenuItem>
          ))}
        </TextField>

        {lectures.map((lec, index) => (
          <Paper key={index} sx={{ p: 2, mt: 3 }} variant="outlined">
            <Typography fontWeight={600} mb={2}>
              Lecture {index + 1}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  required
                  fullWidth
                  label="Lecture Title"
                  value={lec.title}
                  onChange={(e) =>
                    handleLectureChange(index, "title", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  required
                  fullWidth
                  label="Video Link"
                  value={lec.videoUrl}
                  onChange={(e) =>
                    handleLectureChange(index, "videoUrl", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  required
                  select
                  fullWidth
                  label="Status"
                  value={lec.status}
                  onChange={(e) =>
                    handleLectureChange(index, "status", e.target.value)
                  }
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={1}>
                <IconButton
                  color="error"
                  onClick={() => removeLecture(index)}
                  disabled={lectures.length === 1}
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Paper>
        ))}

        <Button startIcon={<AddIcon />} sx={{ mt: 3 }} onClick={addLecture}>
          Add Another Lecture
        </Button>

        <Box mt={4}>
          <Button
            sx={purpleBtnStyle}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save All Lectures"}
          </Button>
        </Box>
      </Paper>

      <ToastContainer position="bottom-center" autoClose={1500} />
    </Box>
  );
};

export default BulkLectures;
